// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import createDebugger from 'debug';
const debug = createDebugger('app:components:Browse'); // eslint-disable-line no-unused-vars
import { ThemeType } from 'trustar-2.0/types';
import { noop } from 'rxjs/util/noop';
import { Link } from 'react-router';
import { List, Map } from 'immutable';
import { Icon, IconLookup, TooltipWrapper } from 'trustar-2.0/components';
import { DropdownWrap, DropdownWrapMenuItem } from './components/DropdownWrap';
import { registerActivity } from 'modules/marketplace/actions';
import { actions as AppActions } from 'modules/app';
import * as Chat from 'modules/chat';
import * as Profile from 'modules/profile';
import { currentBaseRoute } from 'modules/router/getters';
import { LOGOUT_ENDPOINT_AUTHPATH } from 'modules/app/constants';
import { SideMenuLink } from './SideMenuLink';
import { ToggleFeature } from 'trustar-2.0/containers/features';
import { flags as Flags, treatments as Treatments } from 'modules/features';
import {SlackCommunityChatModal} from '../modals/SlackCommunityChatModal';
import {TRUSTAR_WORKFLOWS_LOCALHOST} from 'trustar/utils/constants';

// Style for Main Components (things you may export)
const StyledBrowse = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  background: ${({ theme }) => theme.ThemeColors.SIDE_NAV_BAR.hex};
  justify-content: space-between;
`;

const StyledNavBar = styled.div`
  background: ${({ theme }) => theme.ThemeColors.SIDE_NAV_BAR.hex};
  color: white;
  border: 1px solid ${({ theme }) => theme.ThemeColors.SIDE_NAV_BAR.hex};

  .dropdown .dropdown-menu {
    border-radius: 3px;
    border-width: 0px;
    box-shadow: 1px 1px 0px 1px
      ${({ theme }) => theme.ThemeColors.SIDE_NAV_DROPRIGHT_MENU_ITEM.SHADOW};

    button {
      background: ${({ theme }) =>
        theme.ThemeColors.SIDE_NAV_DROPRIGHT_MENU_ITEM.BACKGROUND};
    }
  }
`;

const NavLinkStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  width: ${({ theme }) => theme.Widths.SIDE_NAV_WIDTH};
  height: ${({ theme }) => theme.Widths.SIDE_NAV_WIDTH};
  flex-grow: 0;
  flex-shrink: 0;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.ThemeColors.SIDE_NAV_BAR.hex};

  &.empty {
    pointer-events: none;
  }

  &:hover,
  &.active {
    .icon {
      background: ${({ theme }) => theme.ThemeColors.SIDE_NAV_BAR_HOVER.hex};
    }
  }

  &[name='Tour'],
  &[name='IOCs'] {
    .icon {
      padding: 0.7rem;
      font-size: 2.65rem;
    }
  }

  &[name='Reports'] {
    .icon {
      padding: 1rem 1.25rem;
    }
  }

  &[name='User Settings'] {
    .icon {
      padding: 0.85rem;
      font-size: 2.35rem;
    }
  }

  &[name='Cases'],
  &[name='Phishing Triage'] {
    .icon {
      padding: 0.85rem;
      font-size: 2.45rem;
    }
  }
  &[name='Workflows'] {
    .icon {
      padding: 0.85rem;
      font-size: 2.6rem;
    }
  }

  .icon {
    margin: 0;
    padding: 1rem;
    color: white;
    font-size: 2rem;
  }
`;

const StyledNavLink = styled(Link)`
  ${NavLinkStyles}
`;

const StyledNavAnchor = styled.a`
  ${NavLinkStyles}
`;

const initialValues = {
  onRegisterClick: noop,
};

// Bindings
type BrowseProps = {
  theme: ThemeType,
  onRegisterClick: Function,
  children?: any,
  currentBaseRoute: string,
  router: Object,
  isTrialUser?: boolean,
};

class Browse extends Component {
  props: BrowseProps;
  static defaultProps = initialValues;
  state = {
    slackCommunityModalOpen: false,
  }
  //  ----Template----

  render() {
    const { theme, isTrialUser, currentBaseRoute } = this.props;

    return (
      <StyledBrowse>
        <StyledNavBar>
          <NavLink
            name={'Reports'}
            to='/browse/reports'
            selector='ReportsTab'
            icon={<Icon icon={IconLookup.REPORTS_ICON} />}
          />
          <NavLink
            name={'IOCs'}
            to='/browse/iocs'
            selector='IndicatorsTab'
            icon={<Icon icon={IconLookup.NAVBAR_IOC} />}
          />
          <ToggleFeature
            feature={Flags.CASES_VIEW_ENABLED}
            match={Treatments.ON}>
            <NavLink
              name={'Cases'}
              to='/browse/cases'
              selector='CasesTab'
              icon={<Icon icon={IconLookup.NAVBAR_CASES} />}
            />
          </ToggleFeature>
          <ToggleFeature
            feature={Flags.PHISHING_TRIAGE_ENABLED}
            match={Treatments.ON}>
            <NavLink
              name={'Phishing Triage'}
              to='/phishing'
              selector='PhishingLink'
              icon={<Icon icon={IconLookup.PHISHING} />}
            />
          </ToggleFeature>
          <ToggleFeature
            feature={Flags.WORKFLOWS_ENABLED}
            match={Treatments.ON}>
            <NavLink
              name={'Workflows'}
              href={TRUSTAR_WORKFLOWS_LOCALHOST ? TRUSTAR_WORKFLOWS_LOCALHOST : '/workflows'}
              selector='WorkflowsLink'
              icon={<Icon icon={IconLookup.WORKFLOWS} />}
            />
          </ToggleFeature>

        </StyledNavBar>
        <StyledNavBar>
          { isTrialUser &&
          <NavLink
            name={'Upgrade'}
            href='https://www.trustar.co/community-plus'
            selector='UpgradeTab'
            rel='noopener noreferrer'
            target='_blank'
            icon={<Icon icon={IconLookup.GIFT} />}
          />
          }
          <NavLink
            name={'Marketplace'}
            to='/browse/marketplace'
            selector='MarketplaceTab'
            onClick={this.props.onRegisterClick}
            icon={<Icon icon={IconLookup.NAVBAR_MARKETPLACE} />}
          />
          <ToggleFeature
            feature={Flags.CHAT_ENABLED}
            match={Treatments.ON}>
            <ChatboxNavLink onClick={this.toggleSlackCommunityModal} />
          </ToggleFeature>
          <ClickableNavLinkWithMenu
            name={'Support'}
            icon={<Icon icon={IconLookup.LIFEBUOY} />}
            menuItems={SupportOptions}
            isActive={currentBaseRoute === 'pages'}
          />
          <ClickableNavLinkWithMenu
            name={'User Settings'}
            icon={<Icon icon={IconLookup.NAVBAR_USER} />}
            menuItems={SettingsOptions}
            isActive={currentBaseRoute === 'settings'}
          />
        </StyledNavBar>
        {this.props.children}
        <SlackCommunityChatModal isOpen={this.state.slackCommunityModalOpen} onExitModal={() => this.toggleSlackCommunityModal(false)} />
      </StyledBrowse>
    );
  }

  // ----Methods----

  // Toggle the modal on/off, if you want to explicitly set the value of the modal's visibility provide a true/false param
  toggleSlackCommunityModal = (hardSetToggleValue?: boolean) => {
    const newState = (hardSetToggleValue === true || hardSetToggleValue === false) ? hardSetToggleValue : !this.state.slackCommunityModalOpen;
    this.setState({ slackCommunityModalOpen: newState});
  };
}

// Logic & Helper Components

const StyledNavBarTooltip = styled.span`
  background: ${({ theme }) => theme.ThemeColors.DARK.hex};
  color: white;
  padding: 0.1rem 0.5rem;
  border-radius: 0.25rem;
`;

const NavLink = (props: Object) => {
  const { preventTooltip, name, icon, children, to, href, ...otherProps } = props;
  const id = (name || '').replace(' ', '');
  const ariaLabel = name + ' Nav Link';
  return (
    <TooltipWrapper
      preventOpen={preventTooltip}
      id={id}
      delay={{ show: 325, hide: 125 }}
      tooltip={<StyledNavBarTooltip> {props.name}</StyledNavBarTooltip>}>
      { to ?
        <StyledNavLink
          to={to}
          name={name}
          aria-label={ariaLabel}
          data-selector={props.selector}
          data-cy={props.selector}
          activeClassName={'active'}
          {...otherProps}>
          {icon}
          {children}
        </StyledNavLink> :
        <StyledNavAnchor
          href={href}
          name={name}
          data-selector={props.selector}
          aria-label={ariaLabel}
          data-cy={props.selector}
          activeClassName={'active'}
          {...otherProps}>
          {icon}
          {children}
        </StyledNavAnchor>
      }
    </TooltipWrapper>
  );
};

const ChatboxNavLink = (props: Object) => {
  const {onClick} = props;
  return (
    <NavLink
      name={'TruSTAR Community'}
      data-cy={'ChatToggleTab'}
      onClick={onClick}
      icon={<Icon icon={IconLookup.BUBBLE_TEXT} />}
    />
  );
};

const SettingsOptions = List([
  <DropdownWrapMenuItem noPadding={true} key={1}>
    <SideMenuLink data-cy={'SettingsPageMenuLink'} activeClassName={'active'} to='/settings'>
      Settings
    </SideMenuLink>
  </DropdownWrapMenuItem>,
  <DropdownWrapMenuItem noPadding={true} key={2}>
    <SideMenuLink data-cy={'LogoutMenuLink'}  href={LOGOUT_ENDPOINT_AUTHPATH}>Logout</SideMenuLink>
  </DropdownWrapMenuItem>
]);

const SupportOptions = List([
  <DropdownWrapMenuItem noPadding={true} key={3}>
    <SideMenuLink data-cy={'SupportPageMenuLink'} href='https://docs.splunk.com/Documentation/SIM/current/Intro/Help' target={'_blank'}>
      Support
    </SideMenuLink>
  </DropdownWrapMenuItem>,
  <DropdownWrapMenuItem noPadding={true} key={1}>
    <SideMenuLink data-cy={'HelpPageMenuLink'}  to='/pages/help'>FAQ</SideMenuLink>
  </DropdownWrapMenuItem>,
  <DropdownWrapMenuItem noPadding={true} key={2}>
    <SideMenuLink
      data-cy={'TermsPageMenuLink'}
      href='https://www.trustar.co/terms-of-use'
      target={'_blank'}>
      Terms Of Use</SideMenuLink>
  </DropdownWrapMenuItem>
]);

const ClickableNavLinkWithMenu = (props: Object) => {
  const { menuItems, name, icon } = props;
  const identifier = `${name.replace(/\s/g, '')}MenuPopout`; // Ensure no spaces
  return (
    <DropdownWrap direction={'right'} menuItems={menuItems} openOnClick={true}>
      <NavLink
        name={name}
        selector={identifier}
        className={props.isActive ? 'active' : null}
        preventTooltip={false}
        icon={icon}
      />
    </DropdownWrap>
  );
};


// All we need to know from state right now is whether chat is open
const mapState = state => ({
  currentBaseRoute: currentBaseRoute(state),
  isTrialUser: Profile.getters.isTrialUser(state),
});

const dispatchProps = {
  onRegisterClick: registerActivity,
  confirm: AppActions.confirm,
};

export default connect(
  mapState,
  dispatchProps
)(Browse);



// WEBPACK FOOTER //
// ./src/app/components/browse/Browse.js